import type { ApiResponse } from '@playforge/common';
import { API_BASE_URL } from './config';

/**
 * Token acquisition for API calls.
 *
 * Flow: App Bridge gives a short-lived Shopify **session token**
 * (`window.shopify.idToken()`). We exchange it once at `POST /auth/session` for a
 * PlayForge JWT, which is cached in memory and attached to subsequent requests.
 * For local development without App Bridge, a PlayForge JWT can be injected via
 * `setDevToken`.
 */
const DEV_TOKEN_KEY = 'playforge.devToken';

let cachedPlatformToken: string | null = null;

export function setDevToken(token: string): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(DEV_TOKEN_KEY, token);
  }
}

export function clearAuth(): void {
  cachedPlatformToken = null;
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(DEV_TOKEN_KEY);
  }
}

/**
 * Evict only the in-memory platform token (not the dev token) so the next call
 * re-exchanges a fresh Shopify session token. Used to recover from a 401 on an
 * expired/rejected token without forcing a full reload.
 */
export function resetPlatformToken(): void {
  cachedPlatformToken = null;
}

async function getShopifyIdToken(): Promise<string | null> {
  if (typeof window !== 'undefined' && window.shopify?.idToken) {
    try {
      return await window.shopify.idToken();
    } catch {
      return null;
    }
  }
  return null;
}

/** Exchange a Shopify session token for a PlayForge JWT via the REST API. */
async function exchangeForPlatformToken(sessionToken: string): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken }),
    });
    const body = (await res.json()) as ApiResponse<{ token: string }>;
    return body.success ? body.data.token : null;
  } catch {
    return null;
  }
}

/**
 * Force a fresh Shopify session-token exchange.
 *
 * `POST /auth/session` is what makes the backend re-run Shopify Token Exchange
 * and replace the stored Admin API token. Normally that only happens on a cold
 * page load, because the PlayForge JWT is cached in memory and stays valid for
 * days — so a merchant whose *Shopify* token went bad (revoked, expired, or a
 * legacy non-expiring one the Admin API now rejects) has no way to recover from
 * inside an open tab: those failures surface as 502s, not the 401 that would
 * trigger the retry path. This gives the UI an explicit "reconnect" action.
 *
 * Returns true when a fresh token was obtained.
 */
export async function reconnectShopify(): Promise<boolean> {
  cachedPlatformToken = null;
  const idToken = await getShopifyIdToken();
  if (!idToken) return false;
  const platformToken = await exchangeForPlatformToken(idToken);
  if (!platformToken) return false;
  cachedPlatformToken = platformToken;
  return true;
}

/**
 * Resolve a PlayForge bearer token for the API, or null if unauthenticated.
 * Caches the exchanged token for the session.
 */
export async function getSessionToken(): Promise<string | null> {
  if (cachedPlatformToken) return cachedPlatformToken;

  const idToken = await getShopifyIdToken();
  if (idToken) {
    const platformToken = await exchangeForPlatformToken(idToken);
    if (platformToken) {
      cachedPlatformToken = platformToken;
      return platformToken;
    }
  }

  if (typeof localStorage !== 'undefined') {
    return localStorage.getItem(DEV_TOKEN_KEY);
  }
  return null;
}
